import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { keywordHubs } from "@/lib/keyword-hubs";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function subscribe(e: React.FormEvent) {
    e.preventDefault();
    // Dynamically import heavy deps (zod + supabase client + sonner) only on
    // first submit so they don't ship in the initial JS bundle.
    const [{ z }, { supabase }, { toast }] = await Promise.all([
      import("zod"),
      import("@/integrations/supabase/client"),
      import("sonner"),
    ]);
    const schema = z.object({ email: z.string().trim().email().max(255) });
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    const { error } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: parsed.data.email });
    setLoading(false);
    if (error && !error.message.includes("duplicate")) {
      toast.error("Something went wrong. Try again.");
      return;
    }
    toast.success("You're on the list.");
    setEmail("");
  }

  return (
    <footer className="border-t border-border/50 mt-32 bg-noir">
      <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-3 gap-12">
        <div>
          <div className="font-display font-bold text-xl">
            RAJ<span className="text-gradient-gold">.</span>SHAMANI
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-sm">
            Entrepreneur, investor, and host of Figuring Out — the podcast helping
            India figure out business, money and life.
          </p>
        </div>
        <div>
          <h2 className="text-xs uppercase tracking-widest text-gold mb-4">Explore</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/about" className="hover:text-foreground">About Raj Shamani</Link></li>
            <li><Link to="/podcast" className="hover:text-foreground">Figuring Out Podcast</Link></li>
            {keywordHubs.map((h) => (
              <li key={h.to}>
                <Link to={h.to} className="hover:text-foreground">{h.title}</Link>
              </li>
            ))}
            <li><Link to="/ventures" className="hover:text-foreground">Ventures & Investments</Link></li>
            <li><Link to="/build-dont-talk" className="hover:text-foreground">Build, Don't Talk (Book)</Link></li>
            <li><Link to="/failure-resume" className="hover:text-foreground">Failure Resume</Link></li>
            <li><Link to="/aion" className="hover:text-foreground">Aion — AI Course</Link></li>
            <li><Link to="/faq" className="hover:text-foreground">FAQ — Age, Net Worth &amp; More</Link></li>
            <li><Link to="/guest" className="hover:text-foreground">Be a Guest</Link></li>
            <li><Link to="/contact" className="hover:text-foreground">Contact</Link></li>
          </ul>
        </div>
        <div>
          <label htmlFor="newsletter-email" className="block">
            <span className="text-xs uppercase tracking-widest text-gold mb-4 block">Newsletter</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Weekly drops. Business, money, lessons. No spam.
          </p>
          <form onSubmit={subscribe} className="flex gap-2">
            <input
              id="newsletter-email"
              type="email"
              required
              aria-label="Email address for newsletter"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 px-3 py-2 rounded-md bg-input border border-border text-sm focus:outline-none focus:ring-1 focus:ring-gold"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-md bg-gold text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              Join
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-border/50 py-6 px-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Raj Shamani. All rights reserved.
      </div>
    </footer>
  );
}
